import React from "react";
import { StyleProp, ViewStyle } from "react-native";
import { ThemedEditor } from "./ThemedEditor";
import { Contact, ContactModel, GroupModel, MediaModel, MemoryModel } from "common247";
import { MentionEntityType } from "./types";

/**
 * Properties for the generic CollaborativeEditor component.
 */
export interface ICollaborativeEditorProps {
  /** Canonical identifier of the entity being edited (e.g. memoryId, mediaId, groupId) */
  entityId?: string;
  /** Domain entity type for room and collection routing (e.g. "memory", "media", "group", "contact") */
  entityType?: "memory" | "media" | "group" | "contact" | string;
  /** Initial text or HTML content to seed the document */
  initialContent?: string;
  /** HTML string content with embedded entity tags (alternative to initialContent) */
  value?: string;
  /** Whether the editor is interactive and editable */
  editable?: boolean;
  /** Emitted whenever the document content updates */
  onChange?: (html: string) => void;
  /** Placeholder displayed when document is empty */
  placeholder?: string;
  /** Optional focus handler to track active editing state */
  onFocus?: () => void;
  /** Optional blur handler to clear active editing state */
  onBlur?: () => void;
  /** Optional container style */
  style?: StyleProp<ViewStyle>;
  /** Optional testID */
  testID?: string;
  /** Contacts available for @ mentions */
  contacts?: (Contact | ContactModel | { id?: string; displayName?: string })[];
  /** Groups available for @ mentions */
  groups?: (GroupModel | { id?: string; name?: string })[];
  /** Media available for + mentions and embeds */
  media?: (MediaModel | { id?: string; title?: string; contentType?: string; downloadUrl?: string })[];
  /** Memories available for # mentions */
  memories?: (MemoryModel | { id?: string; title?: string })[];
  /** Active memory ID for media associations */
  activeMemoryId?: string;
  /** Media upload callback for drag/drop and copy/paste */
  onMediaUpload?: (file: any, memoryId?: string) => Promise<{ id: string; title: string; url?: string }>;
  /** Entity chip press handler */
  onChipPress?: (type: MentionEntityType, id: string, label: string) => void;
  /** Item existence validator */
  isItemFound?: (type: MentionEntityType, id: string) => boolean;
  /** Whether to show raw HTML viewer tab */
  showRawHtml?: boolean;
  /**
   * If true, editor height dynamically fits content between minLines and maxScreenRatio.
   * Defaults to true for all description editors.
   */
  dynamicHeight?: boolean;
  /**
   * Minimum height in lines of normal text. Defaults to 4.
   */
  minLines?: number;
  /**
   * Maximum height as a ratio of screen/window height. Defaults to 0.75 (3/4 of screen height).
   */
  maxScreenRatio?: number;
}

/**
 * Generic Collaborative Description Rich-Text Editor using ThemedEditor, Yjs, WebSockets, and Awareness.
 * Integrates the full rich-text editor with real-time multi-user collaborative functionality across all platforms.
 * All platforms (Web, Android, iOS) use the full ThemedEditor by default.
 *
 * @param {ICollaborativeEditorProps} props - Component properties.
 * @returns {React.JSX.Element} The rendered collaborative editor.
 */
export function CollaborativeEditor(props: ICollaborativeEditorProps): React.JSX.Element {
  // Unified Cross-Platform Standard: All platforms (Web, Android, iOS) use the full ThemedEditor
  return (
    <ThemedEditor
      value={props.initialContent ?? props.value ?? ""}
      onChange={props.onChange}
      editable={props.editable}
      placeholder={props.placeholder}
      entityId={props.entityId}
      entityType={props.entityType}
      collaborative={true}
      contacts={props.contacts}
      groups={props.groups}
      media={props.media}
      memories={props.memories}
      activeMemoryId={props.activeMemoryId || props.entityId}
      onMediaUpload={props.onMediaUpload}
      onChipPress={props.onChipPress}
      isItemFound={props.isItemFound}
      showRawHtml={props.showRawHtml}
      onFocus={props.onFocus}
      onBlur={props.onBlur}
      dynamicHeight={props.dynamicHeight ?? true}
      minLines={props.minLines ?? 4}
      maxScreenRatio={props.maxScreenRatio ?? 0.75}
      style={props.style}
      testID={props.testID}
    />
  );
}
